import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, Button, TextField, IconButton,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import AtsCaptationFlowEditor from '../positions/AtsCaptationFlowEditor';

/**
 * Plantillas de CAPTACIÓN (F6) — nivel proyecto. Calcada en diseño y flujo a
 * AtsPipelineTemplatesTab: guardar flujos del bot (preguntas, mensajes,
 * reglas) con nombre para reutilizarlos al crear vacantes masivas. La marcada
 * como predeterminada se precarga sola en el wizard. Solo admin hacia arriba
 * (gating en AtsConfigurationLayout). Las vacantes copian el flujo al
 * crearse (snapshot): editar o eliminar una plantilla no las afecta.
 */
const AtsCaptationTemplatesTab = () => {
  const { t } = useTranslation();
  const { organization, project, authToken, showAlert } = useStore.getState();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog de crear/editar
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = crear
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftFlow, setDraftFlow] = useState(null);
  const [draftRules, setDraftRules] = useState(null);
  const [savingDialog, setSavingDialog] = useState(false);

  // Dialog de confirmación de borrado
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const authConfig = {
    params: { organizationId: organization, projectId: project },
    headers: { Authorization: `Bearer ${authToken}` },
  };

  useEffect(() => {
    const load = async () => {
      if (!organization || !project) { setLoading(false); return; }
      try {
        setLoading(true);
        const res = await api.get(`/projects/captation-templates`, authConfig);
        const data = res.data.response?.data;
        setTemplates(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        showAlert(t('ats.config.captationTemplates.loadError'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countQuestions = (tpl) => (tpl.captacion?.flow?.questions || []).length;

  // ── Crear / editar ────────────────────────────────────────────────────────

  const handleOpenCreate = () => {
    setEditingId(null);
    setDraftName('');
    setDraftDescription('');
    // Punto de partida: el flujo estándar del sistema (el editor lo muestra
    // con value null; al tocar algo, emite el flow completo)
    setDraftFlow(null);
    setDraftRules(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (tpl) => {
    setEditingId(tpl.id);
    setDraftName(tpl.name || '');
    setDraftDescription(tpl.description || '');
    setDraftFlow(tpl.captacion?.flow ? JSON.parse(JSON.stringify(tpl.captacion.flow)) : null);
    setDraftRules(tpl.captacion?.rules ? JSON.parse(JSON.stringify(tpl.captacion.rules)) : null);
    setDialogOpen(true);
  };

  const handleSaveDialog = async () => {
    const name = draftName.trim();
    if (!name) {
      showAlert(t('ats.config.captationTemplates.nameRequired'), 'warning');
      return;
    }
    if (!draftFlow && !draftRules) {
      showAlert(t('ats.config.captationTemplates.contentRequired'), 'warning');
      return;
    }
    setSavingDialog(true);
    try {
      const body = {
        name,
        description: draftDescription.trim(),
        captacion: { flow: draftFlow, rules: draftRules },
      };
      let res;
      if (editingId) {
        res = await api.put(
          `/projects/captation-templates/${editingId}`, body, authConfig
        );
      } else {
        res = await api.post(
          `/projects/captation-templates`, body, authConfig
        );
      }
      if (res.data.response?.status_response === 'OK') {
        const saved = res.data.response.data;
        setTemplates((prev) => editingId
          ? prev.map((tpl) => (tpl.id === editingId ? saved : tpl))
          : [...prev, saved]);
        setDialogOpen(false);
        showAlert(t('ats.config.captationTemplates.saveSuccess'), 'success');
      } else {
        showAlert(t('ats.config.captationTemplates.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.config.captationTemplates.saveError'), 'error');
    } finally {
      setSavingDialog(false);
    }
  };

  // ── Predeterminada ────────────────────────────────────────────────────────

  const handleToggleDefault = async (tpl) => {
    const newValue = !tpl.isDefault;
    try {
      const res = await api.put(
        `/projects/captation-templates/${tpl.id}`,
        { isDefault: newValue },
        authConfig
      );
      if (res.data.response?.status_response === 'OK') {
        // El backend garantiza una sola default; se replica localmente
        setTemplates((prev) => prev.map((x) => ({
          ...x,
          isDefault: newValue ? x.id === tpl.id : (x.id === tpl.id ? false : x.isDefault),
        })));
      } else {
        showAlert(t('ats.config.captationTemplates.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.config.captationTemplates.saveError'), 'error');
    }
  };

  // ── Eliminar ──────────────────────────────────────────────────────────────

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.delete(
        `/projects/captation-templates/${deleteTarget.id}`, authConfig
      );
      if (res.data.response?.status_response === 'OK') {
        setTemplates((prev) => prev.filter((tpl) => tpl.id !== deleteTarget.id));
        showAlert(t('ats.config.captationTemplates.deleteSuccess'), 'success');
      } else {
        showAlert(t('ats.config.captationTemplates.deleteError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.config.captationTemplates.deleteError'), 'error');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        {t('ats.config.captationTemplates.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ats.config.captationTemplates.description')}
      </Typography>

      {templates.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ border: '1px dashed #D1D5DB', borderRadius: 2, p: 3, textAlign: 'center', mb: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('ats.config.captationTemplates.empty')}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {templates.map((tpl) => (
            <Paper
              key={tpl.id}
              elevation={0}
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: 2,
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {tpl.name}
                  </Typography>
                  {tpl.isDefault && (
                    <Chip
                      label={t('ats.config.captationTemplates.defaultChip')}
                      size="small"
                      sx={{ backgroundColor: '#6A5AF9', color: '#fff', height: 20, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
                {tpl.description && (
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {tpl.description}
                  </Typography>
                )}
              </Box>

              <Chip
                label={tpl.captacion?.flow
                  ? `${countQuestions(tpl)} ${t('ats.config.captationTemplates.questionsCount')}`
                  : t('ats.config.captationTemplates.flowStandard')}
                size="small"
                variant="outlined"
                sx={{ borderColor: '#6A5AF9', color: '#6A5AF9' }}
              />
              {tpl.captacion?.rules?.enabled && (
                <Chip
                  label={t('ats.config.captationTemplates.rulesActive')}
                  size="small"
                  variant="outlined"
                />
              )}

              <Tooltip title={tpl.isDefault
                ? t('ats.config.captationTemplates.unsetDefault')
                : t('ats.config.captationTemplates.setDefault')}
              >
                <IconButton size="small" onClick={() => handleToggleDefault(tpl)}>
                  {tpl.isDefault
                    ? <StarIcon fontSize="small" sx={{ color: '#6A5AF9' }} />
                    : <StarBorderIcon fontSize="small" />}
                </IconButton>
              </Tooltip>

              <Tooltip title={t('ats.config.captationTemplates.edit')}>
                <IconButton size="small" onClick={() => handleOpenEdit(tpl)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('ats.config.captationTemplates.delete')}>
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(tpl)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Box>
      )}

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpenCreate}
        sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
      >
        {t('ats.config.captationTemplates.add')}
      </Button>

      {/* Dialog crear/editar */}
      <Dialog
        open={dialogOpen}
        onClose={() => { if (!savingDialog) setDialogOpen(false); }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId
            ? t('ats.config.captationTemplates.editTitle')
            : t('ats.config.captationTemplates.createTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
            <TextField
              size="small"
              label={t('ats.config.captationTemplates.nameField')}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              required
              sx={{ flex: '0 0 40%' }}
              inputProps={{ maxLength: 80 }}
            />
            <TextField
              size="small"
              label={t('ats.config.captationTemplates.descriptionField')}
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 200 }}
            />
          </Box>
          <Divider sx={{ mb: 2 }} />
          <AtsCaptationFlowEditor
            value={draftFlow}
            onChange={setDraftFlow}
            rules={draftRules}
            onRulesChange={setDraftRules}
            filteringQuestions={[]}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={savingDialog}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDialog}
            disabled={savingDialog || !draftName.trim()}
            startIcon={savingDialog ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminación */}
      <Dialog open={!!deleteTarget} onClose={() => { if (!deleting) setDeleteTarget(null); }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('ats.config.captationTemplates.deleteConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('ats.config.captationTemplates.deleteConfirmText', { name: deleteTarget?.name || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('ats.config.captationTemplates.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AtsCaptationTemplatesTab;
